import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from "react";
import { getPaymentStatus, getUserProfile, reconcileCheckoutSession, finalizePlan } from "../api/authRequest";
import { checkRosterExists } from "../features/athletes/api/athleteRequests";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [userId, setUserId] = useState(() => localStorage.getItem("userId"));
    const [userProfile, setUserProfile] = useState(null);
    const [hasPaid, setHasPaid] = useState(null);
    const [isVerified, setIsVerified] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [hasRoster, setHasRoster] = useState(null);

    // check for roster
   useEffect(() => {
    if (!userId || userProfile?.iiss_type === "emr") return;

    let cancelled = false;
    checkRosterExists(userId)
        .then((res) => {
            if (!cancelled) setHasRoster(res.data.hasRoster);
        })
        .catch((err) => {
            console.error("Failed to check roster status:", err);
            if (!cancelled) setHasRoster(true);
        });

    return () => { cancelled = true; };
}, [userId, userProfile?.iiss_type, isVerified]);

    
    useEffect(() => {
        setLoading(true);
        async function checkPaymentStatus() {
            try {
                if (!userId) {
                setHasPaid(false);
                setIsVerified(false);
                setIsPending(false);
                return;
                }

                // Speculatively try to finalize — no-ops unless payment_info was
                // manually marked 'paid' since we last checked. Replaces the old
                // Stripe-session reconciliation call.
                try {
                await finalizePlan(userId);
                } catch (finalizeErr) {
                console.error("finalizePlan Error", finalizeErr);
                // Fall through — still check current status below.
                }

                const { data } = await getPaymentStatus(userId);
                setHasPaid(!!data.isPaid);
                setIsVerified(!!data.isVerified);
                setIsPending(!!data.isPending);
            } catch (err) {
                console.error("checkPaymentStatus Error", err);
                setHasPaid(false);
                setIsVerified(false);
                setIsPending(false);
            }
            }

        async function fetchUserProfile() {
            try {
                if (!userId) {
                    setUserProfile(null);
                    return;
                }
                const { data } = await getUserProfile(userId);
                setUserProfile(data);
            } catch (err) {
                console.error("fetchUserProfile Error", err);
                setUserProfile(null);
            }
        }

        Promise.all([checkPaymentStatus(), fetchUserProfile()]).finally(() => {
            setLoading(false);
        });
    }, [userId]);

    const login = useCallback((id) => {
        localStorage.setItem("userId", id);
        setUserId(id);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("userId");
        setUserId(null);
        setHasPaid(null);
        setIsVerified(null);
        setUserProfile(null);
        setHasRoster(null);
    }, []);

    const refreshRoster = useCallback(async () => {
        if (!userId) return null;
        try {
            const res = await checkRosterExists(userId);
            setHasRoster(res.data.hasRoster);
            return res.data.hasRoster;
        } catch (err) {
            console.error("Failed to refresh roster status:", err);
            setHasRoster(true);
            return true;
        }
    }, [userId]);

    const ready = !loading;

    const value = useMemo(
        () => ({
            userId,
            login,
            logout,
            ready,
            isAuthenticated: !!userId,
            userProfile,
            hasPaid,
            isVerified,
            isPending,
            setIsVerified,
            hasRoster,
            refreshRoster
        }),
        [userId, login, logout, ready, userProfile, hasPaid, isVerified, isPending, hasRoster, refreshRoster]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}